/**
 * Phase 523 — the app's ONE reader for a dotted path into an upstream JSON value, and the ONE
 * spelling of the `item.` root that addresses an envelope row.
 *
 * It lives in its own module because TWO surfaces now read a row: the mapping half
 * (`populate_group.map`, `choose`) in `lookupModel.ts`, and the ROW FILTER half — a `where`
 * predicate — in `predicates.ts`. `predicates.ts` is the leaf of this folder's import graph
 * (`formModel`, `formDesign` and `lookupModel` all import IT, and it imports nothing back), so the
 * reader cannot be reached from `lookupModel` without making that graph circular, and it must not
 * be re-typed into `predicates.ts` — two path readers with one grammar is exactly how a traversal
 * rule and a safety rule drift apart.
 *
 * This is a READER, not an evaluator — the anti-DSL line the whole form grammar is built on
 * (`predicates.ts` header, `src/fences.test.ts`).
 */

/**
 * How a declaration addresses the array item it is projecting — `populate_group.map` builds a
 * group row from one, `choose` builds a candidate from one, and a source `where` decides whether
 * one becomes a row at all. Mirrors FORM_ROW_FILTER_PATH_ROOTS / FORM_LOOKUP_ITEM_PATH_TEMPLATE in
 * `packages/workflow-schema/src/schema/form-grammar.ts`, which is what deploy-time validation
 * enforces. Deliberately NOT one of FORM_PREDICATE_PATH_ROOTS: `visible_when` is evaluated where
 * no row exists.
 */
export const ITEM_PREFIX = "item.";

/** Path segments that are never traversed, whatever a declaration or an envelope says. */
const UNSAFE_SEGMENTS: ReadonlySet<string> = new Set(["__proto__", "prototype", "constructor"]);

/**
 * Reads a dotted path out of a JSON value: object keys and array indices, own properties only.
 *
 * There is no expression to parse, so an unresolvable path is `undefined` rather than an error —
 * including an absent ROOT, which is what lets a row-filter path resolve to "unset" at a site
 * where no row is in scope. Prototype-bearing segments are refused outright: the payload it walks
 * arrives from an upstream system over an unauthenticated surface.
 */
export function readPath(root: unknown, path: unknown): unknown {
  if (typeof path !== "string" || path.length === 0) return undefined;

  let current: unknown = root;
  for (const segment of path.split(".")) {
    if (segment.length === 0 || UNSAFE_SEGMENTS.has(segment)) return undefined;
    if (current === null || current === undefined) return undefined;

    if (Array.isArray(current)) {
      const index = Number(segment);
      if (!Number.isInteger(index) || index < 0 || index >= current.length) return undefined;
      current = current[index];
      continue;
    }

    if (typeof current !== "object") return undefined;
    if (!Object.prototype.hasOwnProperty.call(current, segment)) return undefined;
    current = (current as Record<string, unknown>)[segment];
  }
  return current;
}
